import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  getCurrentSubscription,
  getBillingPortalUrl,
  getAvailablePlans,
  checkTrialEligibility,
  activateSubscription,
  getRenewalPreview,
  updateBillingPreference,
  resubscribe,
  ResubscribeConflictError,
  UseNewBillingError,
  getCatalogEntries,
  resubscribeWithProduct,
  updateAutoRenewal,
  purchaseTrial,
  type SubscriptionPlan,
  type RenewalPreviewResponse,
  type ResubscribeResponse,
  type BillingInterval,
  type CatalogEntry,
} from "../services/accountService";
import { SubscriptionStatus, PendingProforma } from "../types";
import { getModuleDisplayName } from "../services/api";
import apiClient from "../services/apiClient";
import Layout from "../components/Layout";
import { PlanSelector, type TrialInfo } from "../components/PlanSelector";
import { CatalogPlanSelector } from "../components/CatalogPlanSelector";
import { SubscriptionTimeline } from "../components/SubscriptionTimeline";
import { RenewalPackageModal } from "../components/RenewalPackageModal";
import { useHotel } from "../contexts/HotelContext";
import { useToast } from "../contexts/ToastContext";
import {
  canPurchaseTrial,
  isTrialWithoutAutoProforma,
  TRIAL_PURCHASE_ADMIN_ONLY_MESSAGE,
  TRIAL_PURCHASE_CTA_LABEL,
} from "../utils/trialPurchase";
import {
  TrialPurchasePrompt,
  type TrialPurchaseSummary,
} from "./subscription/TrialPurchasePrompt";

const DEFAULT_SUPPORT_EMAIL = "michal@kukla.tech";

/**
 * Whether the given subscription requires the ordering-model resubscribe flow
 * (CatalogPlanSelector instead of legacy PlanSelector).
 *
 * Covers both `canceled` (user cancellation) and `unpaid` (EXPIRED billing →
 * 'unpaid' mapping from BILLING_TO_LEGACY_STATUS), so pyra-style EXPIRED tenants
 * also see the catalog UI and not the legacy PlanSelector.
 */
const isOrderingResubscribeNeeded = (
  s: Pick<SubscriptionStatus, "billingModel" | "status"> | null | undefined
): boolean =>
  s?.billingModel === "ordering" &&
  (s.status === "canceled" || s.status === "unpaid");

/**
 * Label of the interval confirm button: without automatic proforma (5c) the
 * confirm step IS the purchase; otherwise it updates the open order or
 * changes the preference.
 */
const getConfirmLabel = (
  isTrialPurchase: boolean,
  hasExistingOrder?: boolean
): string => {
  if (isTrialPurchase) return TRIAL_PURCHASE_CTA_LABEL;
  return hasExistingOrder ? "Zaktualizuj zamówienie" : "Potwierdź zmianę";
};

/**
 * Copyable field component with click-to-copy functionality
 */
const CopyableField: React.FC<{
  label: string;
  value: string;
  displayValue?: string;
  mono?: boolean;
  large?: boolean;
}> = ({ label, value, displayValue, mono = false, large = false }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // TODO: Replace with error tracking (Sentry)
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div
      onClick={handleCopy}
      className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-50 transition-colors group"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleCopy()}
      aria-label={`Skopiuj ${label}: ${value}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-500">{label}</p>
        <p
          className={`truncate ${mono ? "font-mono" : ""} ${large ? "text-xl font-bold text-amber-700" : "font-semibold"}`}
        >
          {displayValue || value}
        </p>
      </div>
      <div className="ml-3 flex-shrink-0">
        {copied ? (
          <span className="text-green-600 text-sm font-medium"><span aria-hidden="true">✓</span> Skopiowano</span>
        ) : (
          <span className="text-gray-400 group-hover:text-amber-600 text-sm">
            <span aria-hidden="true">📋</span> Kopiuj
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Bank transfer details section with copy functionality
 */
/**
 * Download PDF via authenticated API client (handles proxy URLs like /billing/self-service/documents/:id/pdf).
 * Direct <a href> navigation would hit the frontend SPA router instead of the backend API.
 */
/**
 * Extract filename from Content-Disposition header, fall back to provided default.
 */
const extractFilename = (headers: Record<string, unknown>, fallback: string): string => {
  const disposition = String(headers?.["content-disposition"] ?? "");
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  return match?.[1] ?? fallback;
};

const handleProformaPdfDownload = async (pdfUrl: string, fallbackFilename: string) => {
  try {
    const response = await apiClient.get(pdfUrl, { responseType: "blob" });
    const filename = extractFilename(response.headers, fallbackFilename);
    const blob = new Blob([response.data], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback: try direct navigation for absolute URLs
    if (pdfUrl.startsWith("https://")) {
      window.open(pdfUrl, "_blank");
    }
  }
};

const BankTransferDetails: React.FC<{
  proforma: PendingProforma;
  formatDate: (date: string | null) => string;
}> = ({ proforma, formatDate }) => {
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const safePdfUrl = proforma.pdfUrl?.startsWith("https://") || proforma.pdfUrl?.startsWith("/") ? proforma.pdfUrl : null;

  const amountFormatted = new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: proforma.currency,
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(proforma.amountGross);

  return (
    <div className="p-6 border-t bg-amber-50">
      <h3 className="text-lg font-semibold text-amber-800 mb-4">
        <span aria-hidden="true">💳</span> Dane do przelewu
      </h3>
      <div className="space-y-2">
        <CopyableField
          label="Numer konta"
          value={proforma.bankAccountNumber}
          mono
        />
        <CopyableField
          label="Odbiorca"
          value={proforma.recipientName}
        />
        <CopyableField
          label="Tytuł przelewu"
          value={proforma.proformaNumber}
          mono
        />
        <CopyableField
          label="Kwota do zapłaty"
          value={new Intl.NumberFormat("pl-PL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(proforma.amountGross)}
          displayValue={amountFormatted}
          large
        />
      </div>
      <div className="mt-4 pt-3 border-t border-amber-200 flex flex-wrap items-center gap-4">
        <p className="text-sm text-gray-600">
          Termin płatności:{" "}
          <span className="font-semibold">{formatDate(proforma.dueDate)}</span>
        </p>
        {safePdfUrl && (
          <button
            disabled={pdfDownloading}
            onClick={async () => {
              setPdfDownloading(true);
              try {
                await handleProformaPdfDownload(safePdfUrl, `proforma-${proforma.proformaNumber}.pdf`);
              } finally {
                setPdfDownloading(false);
              }
            }}
            className="inline-flex items-center px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pdfDownloading ? "Pobieranie..." : <><span aria-hidden="true">📄</span> Pobierz proformę PDF</>}
          </button>
        )}
      </div>
      {proforma.contactEmail && (
        <p className="text-sm text-gray-600 mt-3">
          Po dokonaniu przelewu wyślij potwierdzenie na{" "}
          <a
            href={`mailto:${proforma.contactEmail}`}
            className="text-amber-700 hover:underline font-medium"
          >
            {proforma.contactEmail}
          </a>{" "}
          aby przyspieszyć aktywację dostępu.
        </p>
      )}
    </div>
  );
};

/**
 * AccountSubscription Page
 *
 * Displays current subscription status, billing information, and provides
 * access to Stripe Customer Portal for subscription management.
 *
 * MVP approach: Simple, functional layout without complex UI.
 */
const AccountSubscription: React.FC = () => {
  const { currentTenant } = useHotel();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null);
  const [activating, setActivating] = useState(false);
  const [renewalPreview, setRenewalPreview] = useState<RenewalPreviewResponse | null>(null);
  const [renewalPreviewLoading, setRenewalPreviewLoading] = useState(false);
  const [renewalPreviewError, setRenewalPreviewError] = useState<string | null>(null);
  // Selected interval for the 2-step flow: user clicks interval (preview), then confirms (PATCH)
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval | null>(null);
  // previewLoading: inline spinner while GET /renewal-preview?billingInterval=X is in-flight (AC-12)
  const [previewLoading, setPreviewLoading] = useState<BillingInterval | null>(null);
  // confirmLoading: spinner on confirm button while PATCH /billing-preference is in-flight (AC-13)
  const [confirmLoading, setConfirmLoading] = useState(false);
  // The subscription's original billing interval from the initial load (before any preview overrides)
  const [originalBillingInterval, setOriginalBillingInterval] = useState<BillingInterval | null>(null);
  // successProformaPdfUrl: URL shown after successful PATCH (AC-16)
  const [successProformaPdfUrl, setSuccessProformaPdfUrl] = useState<string | null>(null);
  const [successPdfDownloading, setSuccessPdfDownloading] = useState(false);
  // Expired section state
  // Default "monthly" is a conscious fallback: if preview loading fails (e.g. 503), the user
  // still sees a reasonable default that matches the most common billing interval.
  const [expiredSelectedInterval, setExpiredSelectedInterval] = useState<BillingInterval>("monthly");
  const [expiredPreviewLoading, setExpiredPreviewLoading] = useState<BillingInterval | null>(null);
  // Dedicated renewal preview state for the EXPIRED section — isolated from the shared
  // renewalPreview used by the TRIALING section to prevent cross-section state contamination.
  const [expiredRenewalPreview, setExpiredRenewalPreview] = useState<RenewalPreviewResponse | null>(null);
  const [expiredResubscribeLoading, setExpiredResubscribeLoading] = useState(false);
  const [expiredResubscribeResult, setExpiredResubscribeResult] = useState<ResubscribeResponse | null>(null);
  const [expiredResubscribePdfDownloading, setExpiredResubscribePdfDownloading] = useState(false);
  // Catalog entries for ordering-model resubscribe (AC-3)
  const [catalogEntries, setCatalogEntries] = useState<CatalogEntry[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  // expiredHasConflict: true when 409 received (may be combined with null proformaPdfUrl)
  const [expiredHasConflict, setExpiredHasConflict] = useState(false);
  const [expiredConflictProformaPdfUrl, setExpiredConflictProformaPdfUrl] = useState<string | null>(null);
  const [expiredConflictPdfDownloading, setExpiredConflictPdfDownloading] = useState(false);
  // AC-6: catalog resubscribe conflict state — errorCode='PAYMENT_RECEIVED_CONTACT_SUPPORT' or null (fallback)
  const [catalogConflictErrorCode, setCatalogConflictErrorCode] = useState<string | null>(null);
  const [catalogConflictOrderNumber, setCatalogConflictOrderNumber] = useState<string | null>(null);
  // AC-14: auto-renewal toggle state (absent from API = treat as true for backward compat)
  const [autoRenewalLoading, setAutoRenewalLoading] = useState(false);
  // AC-15: package-on-next-period modal, opened from SubscriptionTimeline's CTA
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  // AC-15: bumped after a successful save to remount SubscriptionTimeline and re-fetch its preview
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const errorRef = useRef<HTMLDivElement>(null);
  const trialingSectionRef = useRef<HTMLDivElement>(null);
  const trialPurchaseButtonRef = useRef<HTMLButtonElement>(null);

  const supportEmail =
    currentTenant?.brand?.replyToAddress || DEFAULT_SUPPORT_EMAIL;

  const brandId = currentTenant?.brand?.id;

  useEffect(() => {
    // Guard: skip until tenant context is resolved to prevent flash of empty state
    if (!brandId) return;
    loadSubscriptionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Focus error message when error occurs (accessibility)
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load current subscription
      const sub = await getCurrentSubscription();
      setSubscription(sub);

      // Determine if plans need to be loaded
      const needsPlansForActivation =
        !sub || sub.status === "canceled" || sub.status === "unpaid" || sub.status === "incomplete";
      const needsPlansForRenewal =
        sub &&
        sub.status === "active" &&
        sub.paymentSource === "bank_transfer";

      // AC-3: For ordering-model resubscribe flow load catalog instead of legacy plans.
      // See isOrderingResubscribeNeeded() for full rationale (covers `canceled` + `unpaid`).
      const needsOrderingResubscribe = isOrderingResubscribeNeeded(sub);

      if (needsOrderingResubscribe) {
        // Load catalog entries from billing catalog API
        if (brandId) {
          setCatalogLoading(true);
          try {
            const catalogResponse = await getCatalogEntries(brandId);
            // Only show plans explicitly marked as available — prevents displaying test/archived catalog entries
            const available = catalogResponse.data.filter(
              (entry) => entry.metadata && typeof entry.metadata === 'object' && (entry.metadata as Record<string, unknown>).available === true
            );
            setCatalogEntries(available);
          } catch (catalogError: any) {
            console.error("Catalog fetch failed:", catalogError);
            setCatalogEntries([]);
          } finally {
            setCatalogLoading(false);
          }
        }
      } else if (needsPlansForActivation || needsPlansForRenewal) {
        // Load available plans (required) — legacy flow
        const plans = await getAvailablePlans();
        setAvailablePlans(plans);

        // Check trial eligibility only for activation flow (not renewal)
        if (needsPlansForActivation) {
          let trialResult: TrialInfo | null = null;
          try {
            trialResult = await checkTrialEligibility();
          } catch (trialError: any) {
            console.warn("Trial eligibility check failed:", trialError);
            // Trial check failed - user will pay immediately without trial period
            trialResult = null;
          }
          setTrialInfo(trialResult);
        }
      }

      // AC-10: treat billingModel === undefined as non-legacy (full UI)
      const isLegacy = sub?.billingModel === 'legacy';

      // Load renewal preview for trialing/active/expired subscriptions with bank_transfer,
      // and for ordering-model canceled/unpaid (EXPIRED billing) subscriptions (AC-4).
      // AC-4/AC-11: skip for legacy tenants — endpoint returns 404 for them
      const isOrderingExpiredOrCanceled = isOrderingResubscribeNeeded(sub);
      const shouldLoadRenewalPreview = !isLegacy && sub &&
        (sub.status === "trialing" || sub.status === "active" || sub.status === "expired" || isOrderingExpiredOrCanceled) &&
        sub.paymentSource === "bank_transfer";

      if (shouldLoadRenewalPreview) {
        setRenewalPreviewLoading(true);
        setRenewalPreviewError(null);
        try {
          const preview = await getRenewalPreview();
          if (sub.status === "expired" || isOrderingExpiredOrCanceled) {
            // EXPIRED (legacy) and ordering-model canceled/unpaid sections share dedicated
            // expiredRenewalPreview state to avoid cross-section state contamination.
            setExpiredRenewalPreview(preview);
            // Initialize expired interval selector with backend-suggested interval
            setExpiredSelectedInterval(preview.billingInterval ?? "monthly");
          } else {
            setRenewalPreview(preview);
            setOriginalBillingInterval(preview.currentOrderInterval ?? preview.billingInterval);
          }
        } catch (previewError: any) {
          console.warn("Renewal preview fetch failed:", previewError);
          // Non-critical — page still renders without renewal preview
          setRenewalPreview(null);
          setRenewalPreviewError(previewError.message || "Nie udało się pobrać danych odnowienia.");
        } finally {
          setRenewalPreviewLoading(false);
        }
      }
    } catch (err: any) {
      console.error("Error loading subscription data:", err);
      setError(err.message || "Błąd wczytywania subskrypcji");
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setManagingSubscription(true);
      const { url } = await getBillingPortalUrl();
      window.location.href = url;
    } catch (err: any) {
      console.error("Error getting billing portal URL:", err);
      addToast({
        type: "error",
        message: err.message || "Błąd otwierania portalu zarządzania subskrypcją",
      });
      setManagingSubscription(false);
    }
  };

  const handleActivatePlan = async (planId: string, stripePriceId: string) => {
    try {
      setActivating(true);

      const { checkoutUrl } = await activateSubscription({
        planId,
        stripePriceId,
      });

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (err: any) {
      // Defensive: backend returns 409 USE_NEW_BILLING for tenants with new billing
      // (cached UI / direct API hit). Redirect to self-service resubscribe instead of error toast.
      if (err instanceof UseNewBillingError) {
        // Reset loading state before navigation so the button does not appear frozen
        // if the redirect is delayed/blocked (CSP, popup blocker, slow network).
        setActivating(false);
        // Toast carries `role="status"` + `aria-live="polite"` (see Toast.tsx:84-97),
        // so screen readers announce the redirect for accessibility.
        addToast({
          type: "info",
          message: "Przekierowuję do odnowienia subskrypcji...",
        });
        // Hard reload zamiast useNavigate() — billing self-service wymaga świeżego
        // sessionContext + invalidacji cache subskrypcji po stronie serwera.
        // Small delay daje toastowi szansę się wyrenderować przed unloadem strony.
        setTimeout(() => {
          window.location.href = err.redirectPath;
        }, 100);
        return;
      }
      console.error("Error activating subscription:", err);
      addToast({
        type: "error",
        message: err.message || "Wystąpił błąd podczas aktywacji subskrypcji",
      });
      setActivating(false);
    }
  };


  /**
   * AC-12: Clicking an interval button triggers ONLY GET /renewal-preview?billingInterval=X.
   * No side effects — pure price calculation.
   */
  const handleSelectInterval = async (billingInterval: BillingInterval) => {
    setSelectedInterval(billingInterval);
    setSuccessProformaPdfUrl(null);
    setPreviewLoading(billingInterval);
    setRenewalPreviewError(null);
    try {
      const preview = await getRenewalPreview(billingInterval);
      setRenewalPreview(preview);
    } catch (err: any) {
      console.error("Error fetching renewal preview:", err);
      setRenewalPreviewError(err.message || "Nie udało się pobrać danych odnowienia.");
    } finally {
      setPreviewLoading(null);
    }
  };

  /**
   * After a successful order change (interval confirm or 5c purchase): refresh
   * subscription + preview without the full-page loading spinner and scroll to
   * the payment details. Never throws — a failed refresh must not turn the
   * already placed order into an error message.
   */
  const refreshAfterOrderChange = async () => {
    setSelectedInterval(null);
    queryClient.invalidateQueries({ queryKey: ['self-service', 'orders'] });
    try {
      const [sub, preview] = await Promise.all([
        getCurrentSubscription(),
        getRenewalPreview(),
      ]);
      setSubscription(sub);
      setRenewalPreview(preview);
      setOriginalBillingInterval(preview.currentOrderInterval ?? preview.billingInterval);
    } catch (err: unknown) {
      console.warn("Refresh after order change failed:", err);
      addToast({
        type: "info",
        message:
          "Nie udało się odświeżyć widoku subskrypcji. Odśwież stronę, aby zobaczyć aktualny stan.",
      });
      return;
    }
    // Scroll to payment details so user sees bank transfer info immediately
    setTimeout(() => {
      trialingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  /**
   * AC-13: Separate confirm button triggers PATCH /billing-preference.
   * AC-16: On success, show proforma PDF link.
   */
  const handleConfirmBillingInterval = async () => {
    if (!selectedInterval) return;
    setConfirmLoading(true);
    setSuccessProformaPdfUrl(null);
    try {
      const result = await updateBillingPreference(selectedInterval);
      if (result.proformaPdfUrl) {
        setSuccessProformaPdfUrl(result.proformaPdfUrl);
      }
      if (result.newOrderCreated && !result.proformaPdfUrl) {
        addToast({
          type: "success",
          message: "Zamówienie złożone. Proforma zostanie przesłana emailem.",
        });
      } else {
        addToast({
          type: "success",
          message: "Interwał rozliczeniowy zaktualizowany.",
        });
      }
      await refreshAfterOrderChange();
    } catch (err: any) {
      console.error("Error confirming billing interval:", err);
      addToast({
        type: "error",
        message: err.message || "Błąd aktualizacji interwału rozliczeniowego",
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  /**
   * 5c: "Kupuję — wystaw proformę" for a trial without automatic proforma
   * (auto-renewal off, e.g. trial invite). Issues the proforma and turns
   * auto-renewal on in one request; then refreshes like the confirm flow.
   */
  const handleTrialPurchase = async (billingInterval?: BillingInterval) => {
    setConfirmLoading(true);
    setSuccessProformaPdfUrl(null);
    try {
      const result = await purchaseTrial(billingInterval, supportEmail);
      // Never confirm the purchase without a proforma document.
      if (!result.proformaPdfUrl) {
        throw new Error(
          "Nie udało się wystawić proformy. Spróbuj ponownie za chwilę."
        );
      }
      setSuccessProformaPdfUrl(result.proformaPdfUrl);
      addToast({
        type: "success",
        message:
          "Zamówienie złożone. Proforma została wystawiona i wysłana e-mailem.",
      });
      await refreshAfterOrderChange();
    } catch (err: unknown) {
      addToast({
        type: "error",
        message:
          err instanceof Error
            ? err.message
            : "Nie udało się złożyć zamówienia. Spróbuj ponownie.",
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  /**
   * 5c: from the trial-end status move to the "Kupuję" call to action
   * (scroll to the trialing section, focus the purchase button when rendered).
   */
  const handleGoToTrialPurchase = () => {
    trialingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    trialPurchaseButtonRef.current?.focus({ preventScroll: true });
  };

  /**
   * Expired section: select billing interval and fetch renewal preview for price display.
   */
  const handleExpiredSelectInterval = async (billingInterval: BillingInterval) => {
    setExpiredSelectedInterval(billingInterval);
    setExpiredPreviewLoading(billingInterval);
    setRenewalPreviewError(null);
    try {
      const preview = await getRenewalPreview(billingInterval);
      setExpiredRenewalPreview(preview);
    } catch (err: any) {
      console.warn("Error fetching renewal preview for expired:", err);
      setRenewalPreviewError(err.message || "Nie udało się pobrać danych odnowienia.");
    } finally {
      setExpiredPreviewLoading(null);
    }
  };

  /**
   * AC-15: after a next-period package change, refresh the timeline and silently reload the
   * subscription — a replaced proforma must leave the bank-transfer details at once.
   */
  const handleRenewalPackageSaved = async () => {
    setTimelineRefreshKey((k) => k + 1);
    try {
      const sub = await getCurrentSubscription();
      setSubscription(sub);
    } catch {
      // Non-critical — the timeline already shows the new target
    }
  };

  /**
   * Expired section: call POST /billing/self-service/resubscribe with selected interval.
   */
  const handleResubscribe = async () => {
    setExpiredResubscribeLoading(true);
    setExpiredHasConflict(false);
    setExpiredConflictProformaPdfUrl(null);
    try {
      const result = await resubscribe(expiredSelectedInterval);
      // Validate proformaPdfUrl before storing — only accept known-safe URL patterns
      const safeResultUrl =
        result.proformaPdfUrl?.startsWith("https://") || result.proformaPdfUrl?.startsWith("/billing/")
          ? result.proformaPdfUrl
          : null;
      setExpiredResubscribeResult({ ...result, proformaPdfUrl: safeResultUrl });
      // Reload subscription to get pendingProforma data for bank transfer details
      try {
        const sub = await getCurrentSubscription();
        setSubscription(sub);
      } catch {
        // Non-critical — success result already captured
      }
    } catch (err: any) {
      if (err instanceof ResubscribeConflictError) {
        // Validate conflict proforma URL before storing
        const safeConflictUrl =
          err.proformaPdfUrl?.startsWith("https://") || err.proformaPdfUrl?.startsWith("/billing/")
            ? err.proformaPdfUrl
            : null;
        setExpiredHasConflict(true);
        setExpiredConflictProformaPdfUrl(safeConflictUrl);
        addToast({
          type: "error",
          message: "Masz już oczekujące zamówienie odnowienia",
        });
      } else {
        console.error("Resubscribe error:", err);
        addToast({
          type: "error",
          message: "Wystąpił błąd. Spróbuj ponownie.",
        });
      }
    } finally {
      setExpiredResubscribeLoading(false);
    }
  };

  /**
   * AC-6: Handle catalog plan selection for ordering-model resubscribe.
   * Calls POST /billing/self-service/resubscribe with productId + billingInterval.
   * HTTP 201 (wasCreated: true) → toast "Zamówienie zostało utworzone".
   * HTTP 200 (wasCreated: false) → toast indicating existing order (idempotency).
   * 409 with errorCode='PAYMENT_RECEIVED_CONTACT_SUPPORT' → inline banner with mailto.
   * 409 without errorCode → fallback "Masz już oczekujące zamówienie" toast.
   */
  const handleCatalogActivate = async (productId: string, billingInterval: BillingInterval) => {
    // Reset previous conflict state before each attempt
    setCatalogConflictErrorCode(null);
    setCatalogConflictOrderNumber(null);
    try {
      setActivating(true);
      const result = await resubscribeWithProduct({ productId, billingInterval });
      if (result.wasCreated === false) {
        addToast({ type: "info", message: "Twoje zamówienie jest już aktywne. Proformę znajdziesz poniżej." });
      } else {
        addToast({ type: "success", message: "Zamówienie zostało utworzone" });
      }
      await loadSubscriptionData();
    } catch (err: any) {
      if (err instanceof ResubscribeConflictError) {
        if (err.errorCode === "PAYMENT_RECEIVED_CONTACT_SUPPORT") {
          // Show inline banner (rendered in JSX below) — no toast
          setCatalogConflictErrorCode(err.errorCode);
          setCatalogConflictOrderNumber(err.orderNumber);
        } else {
          // Fallback for old behaviour before deploy of replace flow
          addToast({ type: "error", message: "Masz już oczekujące zamówienie" });
        }
        return;
      }
      const status = err.response?.status;
      let message = "Wystąpił błąd";
      if (status === 400) message = err.response?.data?.message || `Wybierz plan z katalogu. W razie problemów skontaktuj się z ${supportEmail}`;
      else if (status === 503) message = `Nie udało się wycenić planu. Skontaktuj się z pomocą techniczną pod adresem ${supportEmail}`;
      addToast({ type: "error", message });
    } finally {
      setActivating(false);
    }
  };

  /**
   * AC-14: Toggle auto-renewal for the current tenant's subscription.
   * Calls PATCH /billing/self-service/auto-renewal and updates local state on success.
   * Shows loading state during the request and toast on success/error.
   */
  const handleAutoRenewalToggle = async () => {
    if (!subscription) return;
    const isAutoRenewalEnabled = subscription.autoRenewal ?? true;
    const newValue = !isAutoRenewalEnabled;
    setAutoRenewalLoading(true);
    try {
      const result = await updateAutoRenewal(newValue);
      setSubscription((prev) =>
        prev ? { ...prev, autoRenewal: result.autoRenewal } : prev
      );
      addToast({
        type: "success",
        message: result.autoRenewal
          ? "Automatyczne odnawianie zostało włączone."
          : "Automatyczne odnawianie zostało wyłączone. Otrzymasz email z przypomnieniem przed wygaśnięciem subskrypcji.",
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Nie udało się zmienić ustawienia automatycznego odnawiania.";
      addToast({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setAutoRenewalLoading(false);
    }
  };

  /**
   * Named handler for downloading the conflict proforma PDF (409 path).
   * Extracted from inline JSX to satisfy named-function convention.
   */
  const handleConflictProformaPdfDownload = async () => {
    if (!expiredConflictProformaPdfUrl) return;
    setExpiredConflictPdfDownloading(true);
    try {
      await handleProformaPdfDownload(expiredConflictProformaPdfUrl, "proforma.pdf");
    } finally {
      setExpiredConflictPdfDownloading(false);
    }
  };

  /**
   * Named handler for downloading the proforma PDF from a successful resubscribe (success path).
   * Extracted from inline JSX to satisfy named-function convention.
   */
  const handleSuccessProformaPdfDownload = async () => {
    if (!expiredResubscribeResult?.proformaPdfUrl) return;
    setExpiredResubscribePdfDownloading(true);
    try {
      await handleProformaPdfDownload(expiredResubscribeResult.proformaPdfUrl, "proforma.pdf");
    } finally {
      setExpiredResubscribePdfDownloading(false);
    }
  };

  /**
   * Format a price in cents to a localized currency string.
   * Uses pl-PL locale and ISO 4217 currency code display per project convention.
   */
  const formatCurrency = (cents: number, currency: string): string =>
    new Intl.NumberFormat("pl-PL", {
      style: "currency",
      currency,
      currencyDisplay: "code",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);

  const getBillingIntervalLabel = (interval: BillingInterval): string => {
    const labels: Record<BillingInterval, string> = {
      monthly: "miesięczny",
      quarterly: "kwartalny",
      yearly: "roczny",
    };
    return labels[interval] || interval;
  };

  /**
   * 5c: what "Kupuję" buys — the selector's choice, else the backend default
   * for an empty body (open order's interval, else the preview's). The
   * preview price belongs to preview.billingInterval only.
   */
  const describeTrialPurchase = (
    preview: RenewalPreviewResponse
  ): TrialPurchaseSummary => {
    const interval =
      selectedInterval ?? preview.currentOrderInterval ?? preview.billingInterval;
    return {
      intervalLabel: getBillingIntervalLabel(interval),
      grossAmount:
        interval === preview.billingInterval
          ? formatCurrency(preview.price.grossCents, preview.price.currency)
          : null,
    };
  };

  const getStatusBadge = (status: string, billingModel?: 'legacy' | 'ordering') => {
    const badges: Record<string, { label: string; className: string }> = {
      trialing: {
        label: "Okres próbny",
        className: "bg-blue-100 text-blue-800",
      },
      active: {
        label: "Aktywna",
        className: "bg-green-100 text-green-800",
      },
      past_due: {
        label: "Zaległości w płatności",
        className: "bg-yellow-100 text-yellow-800",
      },
      unpaid: {
        label: "Nieopłacona",
        className: "bg-red-100 text-red-800",
      },
      canceled: {
        label: "Anulowana",
        className: "bg-gray-100 text-gray-800",
      },
      incomplete: {
        label: "Oczekuje na płatność",
        className: "bg-amber-100 text-amber-800",
      },
      expired: {
        label: "Wygasła",
        className: "bg-red-100 text-red-800",
      },
    };

    // Pyra-style override: ordering-model + 'unpaid' is the legacy mapping of EXPIRED
    // (BILLING_TO_LEGACY_STATUS[EXPIRED] = 'unpaid'). User has no payment failure —
    // their trial / period expired. Show "Wygasła" (amber) instead of "Nieopłacona" (red)
    // to avoid suggesting a failed payment. See ux-reviewer Option A.
    const badge =
      status === "unpaid" && billingModel === "ordering"
        ? { label: "Wygasła", className: "bg-amber-100 text-amber-800" }
        : badges[status] || {
            label: status,
            className: "bg-gray-100 text-gray-800",
          };

    return (
      <span
        className={`px-3 py-1 text-sm font-semibold rounded-full ${badge.className}`}
        aria-label={status === "incomplete" ? "Subskrypcja została utworzona, ale oczekuje na wpłatę przelewem bankowym" : undefined}
      >
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto p-6">
          <div
            className="bg-white rounded-lg shadow p-8 text-center"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Wczytywanie subskrypcji...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto p-6">
          <div
            ref={errorRef}
            tabIndex={-1}
            className="bg-red-50 border border-red-200 rounded-lg p-6"
            role="alert"
          >
            <h2 className="text-xl font-semibold text-red-800 mb-2">
              Błąd wczytywania subskrypcji
            </h2>
            <p className="text-red-700">{error}</p>
            <button
              onClick={loadSubscriptionData}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!subscription) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto p-6">
          <h1 className="text-3xl font-bold mb-6">Twoja subskrypcja</h1>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-yellow-800 mb-2">
              Brak subskrypcji
            </h2>
            <p className="text-yellow-700">
              Nie znaleziono aktywnej subskrypcji dla twojego konta.
            </p>
          </div>

          {/* Available Plans Section */}
          <div className="mt-8">
            <h2 className="text-2xl font-bold mb-4">
              Dostępne plany subskrypcji
            </h2>

            {availablePlans.length === 0 ? (
              <p className="text-gray-600">
                Brak dostępnych planów. Skontaktuj się z pomocą techniczną.
              </p>
            ) : (
              <PlanSelector
                plans={availablePlans}
                trialInfo={trialInfo}
                onSelect={handleActivatePlan}
                loading={activating}
              />
            )}
          </div>
        </div>
      </Layout>
    );
  }

  // AC-10: isLegacy uses strict equality — undefined treated as non-legacy
  const isLegacy = subscription?.billingModel === 'legacy';
  // Distinct from `needsOrderingResubscribe`: only the `canceled` subset is used to keep the
  // subscription card visible (legacy `canceled` hides the card, ordering `canceled` keeps it).
  // `unpaid` ordering tenants render the card via the `status !== "canceled"` branch already.
  const showCanceledOrderingCard =
    subscription?.billingModel === 'ordering' && subscription?.status === 'canceled';
  // AC-3 (post-impl fix): include `unpaid` (EXPIRED billing → 'unpaid' mapping) so pyra-style
  // tenants see CatalogPlanSelector / resubscribe section, not the legacy PlanSelector.
  const needsOrderingResubscribe = isOrderingResubscribeNeeded(subscription);

  // Show available plans when:
  // 1. No subscription exists
  // 2. Subscription is canceled
  // 3. Subscription is unpaid
  // 4. Subscription is incomplete (awaiting payment)
  const showPlanSelection =
    !subscription ||
    subscription.status === "canceled" ||
    subscription.status === "unpaid" ||
    subscription.status === "incomplete";

  // AC-14: SubscriptionTimeline guard — same as the "Następna płatność" section it replaces,
  // plus TRIALING (research/timeline-states.md H-15). Card/Stripe and legacy stay unchanged.
  const showSubscriptionTimeline =
    subscription &&
    (subscription.status === "active" || subscription.status === "trialing") &&
    subscription.paymentSource === "bank_transfer" &&
    !isLegacy;

  // 5c: trial without automatic proforma (auto-renewal off, e.g. trial invite)
  // — the customer buys explicitly with "Kupuję — wystaw proformę".
  const isTrialPurchaseOffered = isTrialWithoutAutoProforma(subscription);
  // Nothing to pay yet: the "Kupuję" call to action replaces the payment hints.
  // With a pending proforma (bought, then auto-renewal switched off) the
  // payment-oriented view stays.
  const showTrialPurchaseCta = isTrialPurchaseOffered && !subscription.pendingProforma;
  // Only a tenant admin may buy (trial-purchase is behind TenantAdminGuard).
  const canBuyTrial = canPurchaseTrial(currentTenant);
  const trialEndsAt = subscription.trialEndsAt ?? subscription.currentPeriodEnd;

  // M-3: where the timeline lets the client change the next-period package on their own
  // (ACTIVE with auto-renewal on — trialing and auto-renewal off show no such CTA), the old
  // "Chcesz zmienić plan? Napisz do nas" prompt contradicts it and is replaced. With the feature
  // switched off (AC-16) the timeline hides its CTA, so the old prompt stays.
  const timelineOffersPackageChange =
    showSubscriptionTimeline &&
    subscription.status === "active" &&
    (subscription.autoRenewal ?? true) &&
    renewalPreview?.renewalPreferenceAvailable !== false;

  // Show trialing-specific context section for trialing bank_transfer subscriptions
  const showTrialingSection =
    subscription &&
    subscription.status === "trialing" &&
    subscription.paymentSource === "bank_transfer";

  // Show expired section for expired bank_transfer subscriptions
  // AC-11: hidden for legacy tenants (no renewal-preview endpoint for them)
  const showExpiredSection =
    subscription &&
    subscription.status === "expired" &&
    subscription.paymentSource === "bank_transfer" &&
    !isLegacy;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-3xl font-bold mb-6">Twoja subskrypcja</h1>

        {/* Current Subscription Status */}
        {/* AC-4: Show card for ordering-model canceled (with "Anulowana" badge), hide for legacy canceled.
            `showCanceledOrderingCard` is intentionally narrower than `needsOrderingResubscribe` —
            see explanatory comment near declaration. */}
        {subscription && (subscription.status !== "canceled" || showCanceledOrderingCard) && (
          <div className="bg-white rounded-lg shadow mb-6">
            {/* Plan name + status badge — the timeline's "Bieżący okres" carries both where it renders */}
            {!showSubscriptionTimeline && (
              <div className="p-6 border-b">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-semibold">
                      {subscription.planName}
                    </h2>
                    <p className="text-gray-600 text-sm mt-1">Plan subskrypcji</p>
                  </div>
                  {getStatusBadge(subscription.status, subscription.billingModel)}
                </div>
              </div>
            )}

            {/* AC-14: oś czasu bieżącego i kolejnego okresu — na górze karty, zamiast nagłówka
                z planem, pól z datami (okres, koniec triala) i dawnej sekcji "Następna płatność" */}
            {showSubscriptionTimeline && (
              <div className="p-6 border-b">
                <SubscriptionTimeline
                  key={timelineRefreshKey}
                  status={subscription.status as "active" | "trialing"}
                  currentProductName={subscription.planName}
                  currentPeriodStart={subscription.currentPeriodStart}
                  currentPeriodEnd={
                    subscription.status === "trialing" ? trialEndsAt : subscription.currentPeriodEnd
                  }
                  autoRenewal={subscription.autoRenewal ?? true}
                  onEnableAutoRenewal={handleAutoRenewalToggle}
                  autoRenewalPending={autoRenewalLoading}
                  onOpenPackageModal={() => setIsPackageModalOpen(true)}
                />
              </div>
            )}

            {/* Billing Information */}
            <div className="p-6 grid md:grid-cols-2 gap-6">
              {/* Trial End Date */}
              {subscription.status === "trialing" &&
                subscription.trialEndsAt &&
                !showSubscriptionTimeline && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Koniec okresu próbnego
                    </h3>
                    <p className="text-lg font-semibold">
                      {formatDate(subscription.trialEndsAt)}
                    </p>
                  </div>
                )}

              {/* Next Billing Date — AC-14 (M-3): replaced by SubscriptionTimeline where it renders */}
              {subscription.status === "active" &&
                subscription.currentPeriodEnd &&
                !showSubscriptionTimeline && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Następna płatność
                    </h3>
                    <p className="text-lg font-semibold">
                      {formatDate(subscription.currentPeriodEnd)}
                    </p>
                  </div>
                )}

              {/* Current Period — the timeline's dots carry it where it renders */}
              {subscription.currentPeriodStart && !showSubscriptionTimeline && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Bieżący okres rozliczeniowy
                  </h3>
                  <p className="text-lg">
                    {formatDate(subscription.currentPeriodStart)} -{" "}
                    {formatDate(subscription.currentPeriodEnd)}
                  </p>
                </div>
              )}

              {/* AC-12: Auto-renewal toggle — only for ordering-model subscriptions */}
              {subscription.billingModel === 'ordering' && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    Automatyczne odnawianie
                  </h3>
                  {/* AC-16: Disable toggle for terminal states */}
                  {(subscription.status === 'expired' || subscription.status === 'canceled') ? (
                    <div className="flex items-center gap-2">
                      <span
                        title="Nie można zmienić ustawienia dla zakończonej lub anulowanej subskrypcji"
                        className="inline-block"
                      >
                        {/* AC-13: Custom toggle button */}
                        <button
                          type="button"
                          disabled
                          aria-pressed={(subscription.autoRenewal ?? true)}
                          aria-label="Włącz/wyłącz automatyczne odnawianie"
                          className={`relative inline-flex h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none opacity-50 cursor-not-allowed ${
                            (subscription.autoRenewal ?? true) ? 'bg-primary-700' : 'bg-neutral-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                              (subscription.autoRenewal ?? true) ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </span>
                      <span className="text-sm text-gray-500">
                        {(subscription.autoRenewal ?? true) ? 'Włączone' : 'Wyłączone'}
                      </span>
                      <span className="text-xs text-gray-400">(niedostępne)</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {/* AC-13: Custom toggle button */}
                      <button
                        type="button"
                        onClick={handleAutoRenewalToggle}
                        disabled={autoRenewalLoading}
                        aria-pressed={(subscription.autoRenewal ?? true)}
                        aria-label="Włącz/wyłącz automatyczne odnawianie"
                        className={`relative inline-flex h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                          (subscription.autoRenewal ?? true) ? 'bg-primary-700' : 'bg-neutral-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                            (subscription.autoRenewal ?? true) ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-gray-700">
                        {autoRenewalLoading
                          ? 'Zapisywanie...'
                          : (subscription.autoRenewal ?? true)
                            ? 'Włączone'
                            : 'Wyłączone'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Canceled At */}
              {subscription.canceledAt && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Data anulowania
                  </h3>
                  <p className="text-lg font-semibold">
                    {formatDate(subscription.canceledAt)}
                  </p>
                </div>
              )}

              {/* Cross-links to billing pages (PA-7 AC-14) */}
              <div>
                <Link
                  to="/tenant/company"
                  data-testid="subscription-link-company"
                  className="text-sm text-primary-700 hover:text-primary-800 hover:underline font-medium"
                >
                  Dane firmowe &rarr;
                </Link>
              </div>
              <div>
                <Link
                  to="/tenant/orders"
                  data-testid="subscription-link-orders"
                  className="text-sm text-primary-700 hover:text-primary-800 hover:underline font-medium"
                >
                  Historia zamówień &rarr;
                </Link>
              </div>
            </div>

            {/* Active Modules */}
            <div className="p-6 border-t bg-gray-50">
              <h3 className="text-lg font-semibold mb-3">Aktywne moduły</h3>
              {subscription.enabledModules.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {subscription.enabledModules.map((module) => (
                    <span
                      key={module}
                      className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium"
                    >
                      {getModuleDisplayName(module)}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">
                  Brak aktywnych modułów. Skontaktuj się z pomocą techniczną.
                </p>
              )}
            </div>

            {/* Warnings */}
            {subscription.status === "past_due" && (
              <div className="p-6 border-t bg-yellow-50">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-yellow-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      role="img"
                      aria-label="Ostrzeżenie o problemie z płatnością"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Problem z płatnością
                    </h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      Nie udało się zrealizować płatności. Zaktualizuj metodę
                      płatności, aby kontynuować dostęp do usługi.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {subscription.cancelAtPeriodEnd && (
              <div className="p-6 border-t bg-orange-50">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-orange-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      role="img"
                      aria-label="Informacja o zaplanowanym anulowaniu"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-orange-800">
                      Zaplanowane anulowanie
                    </h3>
                    <p className="mt-1 text-sm text-orange-700">
                      Twoja subskrypcja zostanie anulowana na koniec bieżącego
                      okresu rozliczeniowego.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 5c: trial without automatic proforma is the intended state, not a warning */}
            {showTrialPurchaseCta && (
              <div
                className="p-6 border-t bg-primary-50"
                role="status"
                aria-labelledby="trial-end-status-title"
              >
                <h3
                  id="trial-end-status-title"
                  className="text-sm font-medium text-primary-800"
                >
                  {trialEndsAt
                    ? `Okres próbny kończy się ${formatDate(trialEndsAt)}`
                    : "Okres próbny"}
                </h3>
                <p className="mt-1 text-sm text-primary-700">
                  Proformy nie wystawimy automatycznie — zamówisz ją przyciskiem
                  „{TRIAL_PURCHASE_CTA_LABEL}”, kiedy zdecydujesz.
                </p>
                <button
                  type="button"
                  onClick={handleGoToTrialPurchase}
                  className="mt-2 text-sm font-medium text-primary-700 hover:text-primary-800 hover:underline"
                >
                  Przejdź do zakupu <span aria-hidden="true">&darr;</span>
                </button>
              </div>
            )}

            {/* AC-15: Banner when autoRenewal is false — only for ordering-model subscriptions.
                D-6: superseded by SubscriptionTimeline's "Brak subskrypcji po {Y}" state (#3),
                which the timeline renders for ACTIVE only — TRIALING (state #11) keeps the
                existing trial flow, banner included; kept for card/Stripe and legacy (H-15). */}
            {subscription.billingModel === 'ordering' && subscription.autoRenewal === false && !showTrialPurchaseCta &&
              !(showSubscriptionTimeline && subscription.status === "active") && (
              <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-md" role="alert">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-yellow-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      role="img"
                      aria-label="Informacja o wyłączonym automatycznym odnawianiu"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Automatyczne odnawianie jest wyłączone
                    </h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      Twoja subskrypcja wygaśnie{subscription.currentPeriodEnd ? ` ${formatDate(subscription.currentPeriodEnd)}` : ''} i nie zostanie automatycznie przedłużona.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Bank Transfer Payment Details (skip during trial — shown in trialing section below) */}
            {subscription.paymentSource === "bank_transfer" &&
              subscription.status !== "trialing" &&
              subscription.pendingProforma && (
                <BankTransferDetails
                  proforma={subscription.pendingProforma}
                  formatDate={formatDate}
                />
              )}

            {/* Actions */}
            <div
              className="p-6 border-t"
              role="region"
              aria-label="Zarządzanie subskrypcją"
              aria-live="polite"
            >
              {subscription.paymentSource === "bank_transfer" ? (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <span className="text-primary-700 text-xl mr-3" aria-hidden="true">
                      &#8505;&#65039;
                    </span>
                    <div>
                      <h4 className="text-sm font-medium text-primary-800 mb-1">
                        Informacja o płatności
                      </h4>
                      {isLegacy && subscription.status === "expired" ? (
                        <>
                          <p className="text-primary-800">
                            Twoja subskrypcja wygasła.
                          </p>
                          <p className="text-sm text-primary-700 mt-2">
                            Aby odnowić dostęp, skontaktuj się z nami pod adresem{" "}
                            <a
                              href={`mailto:${supportEmail}`}
                              className="text-primary-800 hover:underline font-medium"
                            >
                              {supportEmail}
                            </a>
                          </p>
                        </>
                      ) : subscription.status === "trialing" ? (
                        <>
                          <p className="text-primary-800">
                            Twoja subskrypcja jest opłacana przelewem bankowym.
                          </p>
                          <p className="text-sm text-primary-700 mt-2">
                            {showTrialPurchaseCta
                              ? "Jeśli nie klikniesz „Kupuję”, dostęp wygaśnie po zakończeniu okresu próbnego. Nie musisz nic pisać ani anulować ręcznie."
                              : "Aby zrezygnować, po prostu nie opłacaj proformy — subskrypcja automatycznie wygaśnie po zakończeniu okresu próbnego. Nie musisz nic pisać ani anulować ręcznie."}
                          </p>
                          <p className="text-sm text-primary-700 mt-2">
                            Masz pytania lub chcesz zmienić plan? Napisz do nas:{" "}
                            <a
                              href={`mailto:${supportEmail}`}
                              className="text-primary-800 hover:underline font-medium"
                            >
                              {supportEmail}
                            </a>
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-primary-800">
                            Twoja subskrypcja jest opłacana przelewem bankowym.
                          </p>
                          <p className="text-sm text-primary-700 mt-2">
                            Aby zrezygnować, po prostu nie opłacaj kolejnej
                            proformy — subskrypcja wygaśnie z końcem bieżącego
                            okresu rozliczeniowego.
                          </p>
                          {timelineOffersPackageChange ? (
                            <p className="text-sm text-primary-700 mt-2">
                              Pakiet na kolejny okres możesz zmienić samodzielnie na osi czasu
                              subskrypcji poniżej. W innych sprawach napisz do nas:{" "}
                              <a
                                href={`mailto:${supportEmail}`}
                                className="text-primary-800 hover:underline font-medium"
                              >
                                {supportEmail}
                              </a>
                            </p>
                          ) : (
                            <p className="text-sm text-primary-700 mt-2">
                              Chcesz zmienić plan? Napisz do nas:{" "}
                              <a
                                href={`mailto:${supportEmail}`}
                                className="text-primary-800 hover:underline font-medium"
                              >
                                {supportEmail}
                              </a>
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleManageSubscription}
                    disabled={managingSubscription}
                    aria-busy={managingSubscription}
                    className="w-full md:w-auto px-6 py-3 bg-primary-700 text-white font-semibold rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {managingSubscription
                      ? "Przekierowywanie..."
                      : "Zarządzaj subskrypcją"}
                  </button>
                  <p className="text-sm text-gray-600 mt-3">
                    Kliknij aby zarządzać swoją subskrypcją, zmienić plan,
                    zaktualizować metodę płatności lub anulować subskrypcję.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Trialing Section (AC-E6) - bank_transfer trialing */}
        {showTrialingSection && (
          <div ref={trialingSectionRef} className="bg-white rounded-lg shadow mt-6">
            {isLegacy ? (
              // AC-5/AC-6/AC-12: legacy trialing — no renewal preview, show plan name + proforma or fallback
              <>
                <div className="p-6 border-b">
                  <h2 className="text-2xl font-semibold">
                    Szczegóły płatności
                  </h2>
                </div>
                {subscription.pendingProforma ? (
                  // AC-6: legacy trialing with proforma — show BankTransferDetails
                  <BankTransferDetails
                    proforma={subscription.pendingProforma}
                    formatDate={formatDate}
                  />
                ) : (
                  // AC-12: legacy trialing without proforma — show fallback message
                  <div className="p-6">
                    <p className="text-sm text-gray-600">
                      Szczegóły dotyczące płatności zostaną wysłane emailem ok. 7 dni przed końcem okresu próbnego.
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      W razie pytań skontaktuj się z nami:{" "}
                      <a href={`mailto:${supportEmail}`} className="text-primary-700 hover:underline">
                        {supportEmail}
                      </a>
                    </p>
                  </div>
                )}
              </>
            ) : subscription.pendingProforma ? (
              // AC-E4 + AC-E6: trialing z proformą — dane do przelewu
              <>
                <div className="p-6 border-b">
                  <h2 className="text-2xl font-semibold">
                    Twój plan po zakończeniu okresu próbnego
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">
                    Proforma do opłacenia przelewem jest już dostępna.
                  </p>
                </div>
                {renewalPreview && (
                  <div className="p-6 border-b grid md:grid-cols-2 gap-4">
                    {/* AC-E1: Wybrany interwał rozliczeniowy */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">
                        Interwał rozliczeniowy
                      </h3>
                      <p className="text-lg font-semibold capitalize">
                        {getBillingIntervalLabel(renewalPreview.currentOrderInterval ?? renewalPreview.billingInterval)}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">
                        Plan po trialu
                      </h3>
                      <p className="text-lg font-semibold">
                        {renewalPreview.renewalProduct.name}
                      </p>
                    </div>
                  </div>
                )}
                <BankTransferDetails
                  proforma={subscription.pendingProforma}
                  formatDate={formatDate}
                />
              </>
            ) : (
              // AC-E6: trialing bez proformy — info o planie i opcja zmiany interwału
              <>
                <div className="p-6 border-b">
                  <h2 className="text-2xl font-semibold">
                    Twój plan po zakończeniu okresu próbnego
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">
                    {isTrialPurchaseOffered
                      ? "Okres próbny nie przedłuży się automatycznie i nie wystawimy proformy bez Twojej decyzji."
                      : "Po zakończeniu okresu próbnego Twoja subskrypcja zostanie automatycznie przedłużona."}
                  </p>
                </div>
                <div className="p-6">
                  {renewalPreviewLoading ? (
                    <div className="flex items-center gap-3 py-4" aria-live="polite" aria-busy="true">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                      <p className="text-gray-600">Ładowanie danych...</p>
                    </div>
                  ) : renewalPreviewError ? (
                    <div className="py-2" role="alert">
                      {/* AC-17: Display exact error message (e.g. 503 pricing unavailable) */}
                      <p className="text-red-700 text-sm mb-2">
                        {renewalPreviewError}
                      </p>
                      <button
                        onClick={loadSubscriptionData}
                        className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Spróbuj ponownie
                      </button>
                    </div>
                  ) : renewalPreview ? (
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* AC-E1: Wybrany interwał rozliczeniowy */}
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-1">
                            Interwał rozliczeniowy
                          </h3>
                          <p className="text-lg font-semibold capitalize">
                            {getBillingIntervalLabel(renewalPreview.currentOrderInterval ?? renewalPreview.billingInterval)}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-1">
                            Plan po trialu
                          </h3>
                          <p className="text-lg font-semibold">
                            {renewalPreview.renewalProduct.name}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-1">
                            Cena netto
                          </h3>
                          <p className="text-lg font-semibold text-gray-900">
                            {previewLoading !== null ? (
                              <span className="inline-flex items-center gap-1.5 text-gray-500">
                                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" aria-hidden="true"></span>
                                <span className="text-sm">Przeliczam...</span>
                              </span>
                            ) : (
                              formatCurrency(renewalPreview.price.netCents, renewalPreview.price.currency)
                            )}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-1">
                            Cena brutto
                          </h3>
                          <p className="text-lg font-semibold text-amber-700">
                            {previewLoading !== null ? (
                              <span className="inline-flex items-center gap-1.5 text-gray-500">
                                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" aria-hidden="true"></span>
                                <span className="text-sm">Przeliczam...</span>
                              </span>
                            ) : (
                              formatCurrency(renewalPreview.price.grossCents, renewalPreview.price.currency)
                            )}
                          </p>
                        </div>
                      </div>

                      {/* 5c: trial without automatic proforma — explicit purchase */}
                      {isTrialPurchaseOffered ? (
                        <TrialPurchasePrompt
                          summary={describeTrialPurchase(renewalPreview)}
                          isPricing={previewLoading !== null}
                          isSubmitting={confirmLoading}
                          canPurchase={canBuyTrial}
                          onPurchase={() => handleTrialPurchase(selectedInterval ?? undefined)}
                          buttonRef={trialPurchaseButtonRef}
                        />
                      ) : (
                        /* AC-E3: Data spodziewanej proformy */
                        <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
                          <p className="text-sm text-primary-800">
                            Proforma zostanie wysłana ok.{" "}
                            <span className="font-semibold">
                              {new Date(renewalPreview.proformaExpectedAt).toLocaleDateString("pl-PL", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                            </span>
                          </p>
                        </div>
                      )}

                    </div>
                  ) : null}
                </div>
              </>
            )}

            {/* AC-12/AC-13/AC-14/AC-18: 2-step flow: select interval → confirm */}
            {/* Shown regardless of pendingProforma — user can always change interval during trial */}
            {renewalPreview?.canChangeBillingInterval && (
              <div className="p-6 border-t">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  {isTrialPurchaseOffered
                    ? "Wybierz interwał zakupu"
                    : "Zmień interwał rozliczeniowy"}
                </h3>
                {/* AC-18: role="radiogroup" + role="radio" + aria-checked */}
                <div
                  className="flex flex-wrap gap-2"
                  role="radiogroup"
                  aria-label="Interwał rozliczeniowy"
                >
                  {(["monthly", "quarterly", "yearly"] as BillingInterval[]).map((interval) => {
                    const activeInterval = selectedInterval ?? originalBillingInterval ?? renewalPreview.billingInterval;
                    const isActive = interval === activeInterval;
                    return (
                      <button
                        key={interval}
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => handleSelectInterval(interval)}
                        disabled={previewLoading !== null || confirmLoading}
                        aria-busy={previewLoading === interval}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                          isActive
                            ? "border-primary-500 bg-primary-50 text-primary-800"
                            : "border-gray-300 bg-white text-gray-700 hover:border-primary-400 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        }`}
                      >
                        {previewLoading === interval ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current" aria-hidden="true"></span>
                            {getBillingIntervalLabel(interval)}
                          </span>
                        ) : (
                          getBillingIntervalLabel(interval)
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* AC-17: Inline error for pricing unavailable (503) */}
                {renewalPreviewError && (
                  <div className="mt-2" role="alert">
                    <p className="text-red-700 text-sm">{renewalPreviewError}</p>
                  </div>
                )}

                {/* AC-14: Confirm button visible ONLY when selected interval differs from current */}
                {(() => {
                  const effectiveCurrentInterval = originalBillingInterval ?? renewalPreview.billingInterval;
                  const showConfirm =
                    selectedInterval !== null &&
                    selectedInterval !== effectiveCurrentInterval;
                  if (!showConfirm) return null;
                  // 5c: the "Kupuję" button above already buys the selected interval
                  if (showTrialPurchaseCta) return null;
                  const confirmLabel = getConfirmLabel(
                    isTrialPurchaseOffered,
                    renewalPreview.hasExistingOrder
                  );
                  // 5c: the purchase is for a tenant admin only
                  const purchaseBlocked = isTrialPurchaseOffered && !canBuyTrial;
                  return (
                    <div className="mt-3">
                      <button
                        onClick={
                          isTrialPurchaseOffered
                            ? () => handleTrialPurchase(selectedInterval)
                            : handleConfirmBillingInterval
                        }
                        disabled={confirmLoading || previewLoading !== null || !!renewalPreviewError || purchaseBlocked}
                        aria-busy={confirmLoading}
                        aria-describedby={purchaseBlocked ? "trial-purchase-confirm-admin-only" : undefined}
                        className="px-5 py-2 bg-primary-700 text-white text-sm font-medium rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {confirmLoading ? "Składam zamówienie..." : confirmLabel}
                      </button>
                      {purchaseBlocked && (
                        <p id="trial-purchase-confirm-admin-only" className="mt-2 text-sm text-gray-600">
                          {TRIAL_PURCHASE_ADMIN_ONLY_MESSAGE}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* AC-16: Success message + proforma PDF link */}
                {successProformaPdfUrl && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg" role="status">
                    <p className="text-sm text-green-800 font-medium">
                      Zamówienie złożone. Proforma dostępna do pobrania.
                    </p>
                    <button
                      disabled={successPdfDownloading}
                      onClick={async () => {
                        setSuccessPdfDownloading(true);
                        try {
                          await handleProformaPdfDownload(successProformaPdfUrl, "proforma.pdf");
                        } finally {
                          setSuccessPdfDownloading(false);
                        }
                      }}
                      className="inline-flex items-center mt-2 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {successPdfDownloading ? "Pobieranie..." : <><span aria-hidden="true">📄</span> Pobierz proformę PDF</>}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Expired Section — bank_transfer expired: interval selector + resubscribe */}
        {showExpiredSection && (
          <div
            className="bg-white rounded-lg shadow mt-6"
            role="region"
            aria-label="Wznowienie subskrypcji"
          >
            <div className="p-6 border-b">
              <h2 className="text-2xl font-semibold text-red-800">
                Twoja subskrypcja wygasła
              </h2>
              {subscription?.currentPeriodEnd && (
                <p className="text-gray-500 text-sm mt-0.5">
                  Wygasła dnia: {formatDate(subscription.currentPeriodEnd)}
                </p>
              )}
              <p className="text-gray-600 text-sm mt-1">
                Wybierz interwał rozliczeniowy i wznów subskrypcję przelewem bankowym.
              </p>
            </div>

            {/* Price preview */}
            <div className="p-6 border-b">
              {renewalPreviewLoading ? (
                <div className="flex items-center gap-3 py-4" aria-live="polite" aria-busy="true">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                  <p className="text-gray-600">Ładowanie danych...</p>
                </div>
              ) : renewalPreviewError ? (
                <div className="py-2" role="alert">
                  <p className="text-red-700 text-sm mb-2">{renewalPreviewError}</p>
                  <button
                    onClick={loadSubscriptionData}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Spróbuj ponownie
                  </button>
                </div>
              ) : expiredRenewalPreview ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Produkt
                    </h3>
                    <p className="text-lg font-semibold">
                      {expiredRenewalPreview.renewalProduct.name || "Subskrypcja"}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Cena netto
                    </h3>
                    <p className="text-lg font-semibold text-gray-900">
                      {expiredPreviewLoading !== null ? (
                        <span className="inline-flex items-center gap-1.5 text-gray-500">
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" aria-hidden="true"></span>
                          <span className="text-sm">Przeliczam...</span>
                        </span>
                      ) : (
                        formatCurrency(expiredRenewalPreview.price.netCents, expiredRenewalPreview.price.currency)
                      )}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Cena brutto
                    </h3>
                    <p className="text-lg font-semibold text-amber-700">
                      {expiredPreviewLoading !== null ? (
                        <span className="inline-flex items-center gap-1.5 text-gray-500">
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" aria-hidden="true"></span>
                          <span className="text-sm">Przeliczam...</span>
                        </span>
                      ) : (
                        formatCurrency(expiredRenewalPreview.price.grossCents, expiredRenewalPreview.price.currency)
                      )}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 md:col-span-2">Cena netto + VAT 23%</p>
                </div>
              ) : null}
            </div>

            {/* Interval selector + resubscribe button */}
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Interwał rozliczeniowy
              </h3>
              <div
                className="flex flex-wrap gap-2"
                role="radiogroup"
                aria-label="Interwał rozliczeniowy"
              >
                {(["monthly", "quarterly", "yearly"] as BillingInterval[]).map((interval) => {
                  const isActive = interval === expiredSelectedInterval;
                  return (
                    <button
                      key={interval}
                      role="radio"
                      aria-checked={isActive}
                      onClick={() => handleExpiredSelectInterval(interval)}
                      disabled={expiredPreviewLoading !== null || expiredResubscribeLoading}
                      aria-busy={expiredPreviewLoading === interval}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                        isActive
                          ? "border-primary-500 bg-primary-50 text-primary-800"
                          : "border-gray-300 bg-white text-gray-700 hover:border-primary-400 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      }`}
                    >
                      {expiredPreviewLoading === interval ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current" aria-hidden="true"></span>
                          {getBillingIntervalLabel(interval)}
                        </span>
                      ) : (
                        getBillingIntervalLabel(interval)
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Resubscribe button */}
              {!expiredResubscribeResult && (
                <div className="mt-4">
                  <button
                    onClick={handleResubscribe}
                    disabled={expiredResubscribeLoading || expiredPreviewLoading !== null}
                    aria-busy={expiredResubscribeLoading}
                    className="px-6 py-3 bg-primary-700 text-white font-semibold rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {expiredResubscribeLoading ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" aria-hidden="true"></span>
                        Wznawiam...
                      </>
                    ) : (
                      "Wznów subskrypcję"
                    )}
                  </button>
                </div>
              )}

              {/* 409 conflict: link to existing proforma or fallback message */}
              {expiredHasConflict && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg" role="alert">
                  <p className="text-sm text-yellow-800 font-medium">
                    Masz już oczekujące zamówienie odnowienia.
                  </p>
                  {expiredConflictProformaPdfUrl ? (
                    <button
                      disabled={expiredConflictPdfDownloading}
                      onClick={handleConflictProformaPdfDownload}
                      className="inline-flex items-center mt-2 px-3 py-1.5 bg-yellow-600 text-white text-sm font-medium rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {expiredConflictPdfDownloading ? "Pobieranie..." : "Pobierz istniejącą proformę PDF"}
                    </button>
                  ) : (
                    <p className="text-sm text-yellow-700 mt-1">
                      Proforma zostanie wygenerowana wkrótce — sprawdź email.
                    </p>
                  )}
                </div>
              )}

              {/* Success: bank transfer details */}
              {expiredResubscribeResult && (
                <div className="mt-4" role="status">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
                    <p className="text-sm text-green-800 font-medium">
                      Zamówienie złożone. Poniżej znajdziesz dane do przelewu.
                    </p>
                    {expiredResubscribeResult.proformaPdfUrl && (
                      <button
                        disabled={expiredResubscribePdfDownloading}
                        onClick={handleSuccessProformaPdfDownload}
                        className="inline-flex items-center mt-2 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {expiredResubscribePdfDownloading ? "Pobieranie..." : "Pobierz proformę PDF"}
                      </button>
                    )}
                  </div>
                  {subscription?.pendingProforma ? (
                    <BankTransferDetails
                      proforma={subscription.pendingProforma}
                      formatDate={formatDate}
                    />
                  ) : (
                    <p className="text-sm text-gray-600 mt-2">
                      Dane do przelewu zostaną wysłane na email.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* AC-15: modal wyboru pakietu na kolejny okres, otwierany z CTA osi czasu */}
        {showSubscriptionTimeline && (
          <RenewalPackageModal
            isOpen={isPackageModalOpen}
            onClose={() => setIsPackageModalOpen(false)}
            onSaved={handleRenewalPackageSaved}
            currentModules={subscription.enabledModules}
            pendingProforma={
              subscription.pendingProforma
                ? {
                    number: subscription.pendingProforma.proformaNumber,
                    amountGross: subscription.pendingProforma.amountGross,
                    currency: subscription.pendingProforma.currency,
                  }
                : null
            }
          />
        )}

        {/* AC-6: Catalog plan selector for ordering-model canceled OR unpaid (EXPIRED) subscriptions */}
        {showPlanSelection && needsOrderingResubscribe && catalogLoading && (
          <div className="bg-white rounded-lg shadow mt-6">
            <div className="p-6 text-center" aria-live="polite" aria-busy="true">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-3 text-gray-600">Ładowanie dostępnych planów...</p>
            </div>
          </div>
        )}
        {showPlanSelection && needsOrderingResubscribe && !catalogLoading && catalogEntries.length > 0 && (
          <div className="bg-white rounded-lg shadow mt-6">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-semibold">
                {subscription?.status === 'unpaid'
                  ? 'Twoja subskrypcja wygasła'
                  : 'Twoja subskrypcja została anulowana'}
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                Wybierz plan z naszego katalogu, aby ją wznowić.
              </p>
            </div>
            <div className="p-6">
              <CatalogPlanSelector
                entries={catalogEntries}
                onSelect={handleCatalogActivate}
                loading={activating}
                disabled={activating}
                currentOrderInterval={expiredRenewalPreview?.currentOrderInterval ?? null}
                currentOrderNumber={expiredRenewalPreview?.currentOrderNumber ?? null}
                enabledModules={subscription?.enabledModules}
              />

              {/* AC-6: Inline banner for PAYMENT_RECEIVED_CONTACT_SUPPORT conflict */}
              {catalogConflictErrorCode === "PAYMENT_RECEIVED_CONTACT_SUPPORT" && (() => {
                // L2: defense-in-depth — whitelist order number format before injecting into mailto body
                // L7: fallback body when order number is null/invalid (avoids empty support email)
                const isValidOrderNumber =
                  !!catalogConflictOrderNumber &&
                  /^[A-Za-z0-9/_-]{1,64}$/.test(catalogConflictOrderNumber);
                const body = isValidOrderNumber
                  ? `Numer zamówienia: ${catalogConflictOrderNumber}`
                  : "Proszę o pomoc w sprawie zamówienia z zarejestrowaną płatnością.";
                return (
                  <div
                    role="alert"
                    className="mt-4 p-4 bg-amber-50 border border-amber-300 rounded-lg"
                  >
                    <p className="text-sm font-medium text-amber-800">
                      Wpłynęła płatność na poprzednie zamówienie. Skontaktuj się z pomocą techniczną.
                    </p>
                    <a
                      href={`mailto:${supportEmail}?subject=${encodeURIComponent("Pomoc - zamówienie z płatnością")}&body=${encodeURIComponent(body)}`}
                      className="inline-flex items-center mt-2 text-sm font-medium text-amber-700 underline hover:text-amber-900"
                    >
                      Napisz do {supportEmail}
                    </a>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* AC-6: Empty catalog message for ordering-model */}
        {showPlanSelection && needsOrderingResubscribe && !catalogLoading && catalogEntries.length === 0 && (
          <div className="mt-8">
            <p className="text-gray-600">
              Brak dostępnych planów. Skontaktuj się z nami pod adresem{" "}
              <a href={`mailto:${supportEmail}`} className="text-primary-700 hover:underline">{supportEmail}</a>.
            </p>
          </div>
        )}

        {/* Available Plans Section — legacy flow */}
        {showPlanSelection && !needsOrderingResubscribe && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold mb-4">
              Dostępne plany subskrypcji
            </h2>

            {availablePlans.length === 0 ? (
              <p className="text-gray-600">
                Brak dostępnych planów. Skontaktuj się z pomocą techniczną.
              </p>
            ) : (
              <PlanSelector
                plans={availablePlans}
                trialInfo={trialInfo}
                onSelect={handleActivatePlan}
                loading={activating}
              />
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AccountSubscription;
